/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ConferenceProfileFormView.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Conference Profile Form component for creating and editing FreeSWITCH conference profiles.
    Features comprehensive parameter management with JSONB storage and XML generation.

 🔧 Notes:
    - Supports all FreeSWITCH conference parameters
    - Uses JSONB for flexible parameter storage
    - Generates XML automatically via database triggers
    - Organized in logical tabs for better UX
    - Integrates with debug mode for field visibility

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useDebugMode } from '../../hooks/useDebugMode';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

// Components
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { FormBox } from '../../components/ui/FormBox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ModuleFormHeader } from '../../components/layouts';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { navigateToConferenceProfileDuplicate } from './conferenceProfilesActions';
import {
  Plus,
  Settings,
  Users,
  Search,
  Copy
} from 'lucide-react';
import { ConferenceProfile, ConferenceProfileSettings, ConferenceProfileFormState } from '../conferenceProfiles/types';

type FormConferenceProfile = ConferenceProfileFormState;
import { fetchAllConferenceProfiles } from '../conferenceProfiles/api';

interface ConferenceProfileFormViewProps {
  onBack: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave: (profileData: any) => Promise<any>;
  onDelete?: () => Promise<void>;
  profile?: ConferenceProfile | null;
  mode: 'create' | 'edit';
}

const DEFAULT_PROFILE_SETTINGS: Readonly<ConferenceProfileSettings> = Object.freeze({
  rate: '8000',
  channels: '1',
  energyLevel: 100,
  mohSound: '$${hold_music}',
  enterSound: 'tone_stream://%(200,0,500,600,700)',
  exitSound: 'tone_stream://%(500,0,300,200,100,50,25)',
  comfortNoise: true,
  videoMode: 'auto',
  videoLayout: '1x1',
  videoCodec: 'VP8',
  maxVideoParticipants: 9,
  mutedSound: 'tone_stream://%(500,0,300,200,100,50,25)',
  unmutedSound: 'tone_stream://%(200,0,500,600,700)',
  aloneSound: 'tone_stream://%(1000,0,400,200,100,50,25)',
  kickedSound: 'tone_stream://%(200,0,100,50,25)',
  conferenceFlags: 'mute|deaf|moderator',
  callerControls: 'none',
  moderatorControls: true,
  memberFlags: 'waste|mute|deaf|moderator',
  muteDigits: '0',
  deafMuteDigits: '*',
  energyUpDigits: '9',
  energyEquDigits: '8',
  energyDnDigits: '7',
  volTalkUpDigits: '3',
  volTalkZeroDigits: '2',
  volTalkDnDigits: '1',
  volListenUpDigits: '6',
  volListenZeroDigits: '5',
  volListenDnDigits: '4',
  hangupDigits: '#'
});

const createEmptyProfile = (): FormConferenceProfile => ({
  name: '',
  description: '',
  enabled: true,
  isSystem: false,
  settings: { ...DEFAULT_PROFILE_SETTINGS }
});

const coerceSettings = (settings: Record<string, unknown> | null | undefined): ConferenceProfileSettings => {
  const result: ConferenceProfileSettings = { ...(DEFAULT_PROFILE_SETTINGS as ConferenceProfileSettings) };
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return result;
  }

  for (const [key, value] of Object.entries(settings)) {
    if (!(key in result)) {
      continue;
    }

    const typedKey = key as keyof ConferenceProfileSettings;

    switch (typedKey) {
      case 'energyLevel':
      case 'maxVideoParticipants': {
        const numericValue =
          typeof value === 'number'
            ? value
            : typeof value === 'string'
              ? Number(value)
              : Number.NaN;
        if (!Number.isNaN(numericValue)) {
          (result as any)[typedKey] = numericValue;
        }
        break;
      }
      case 'comfortNoise':
      case 'moderatorControls': {
        if (typeof value === 'boolean') {
          (result as any)[typedKey] = value;
        } else if (typeof value === 'string') {
          (result as any)[typedKey] = value.toLowerCase() === 'true';
        }
        break;
      }
      default: {
        if (typeof value === 'string') {
          (result as any)[typedKey] = value;
        } else if (value !== null && value !== undefined) {
          (result as any)[typedKey] = String(value);
        }
      }
    }
  }

  return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapApiProfile = (raw: any): FormConferenceProfile => {
  const empty = createEmptyProfile();
  const mergedSettings = coerceSettings(raw?.settings as Record<string, unknown> | null | undefined);

  return {
    id: typeof raw?.id === 'number' ? raw.id : undefined,
    uuid: typeof raw?.uuid === 'string' ? raw.uuid : undefined,
    name: typeof raw?.name === 'string' ? raw.name : '',
    description: typeof raw?.description === 'string' ? raw.description : '',
    enabled: typeof raw?.enabled === 'boolean' ? raw.enabled : Boolean(raw?.enabled ?? true),
    isSystem: typeof raw?.isSystem === 'boolean'
      ? raw.isSystem
      : typeof raw?.is_system === 'boolean'
        ? raw.is_system
        : false,
    settings: mergedSettings,
    created_at: typeof raw?.createdAt === 'string' ? raw.createdAt : raw?.created_at,
    created_by: raw?.createdBy ?? raw?.created_by,
    updated_at: typeof raw?.updatedAt === 'string' ? raw.updatedAt : raw?.updated_at,
    updated_by: raw?.updatedBy ?? raw?.updated_by
  };
};

import { ConferenceCallerControlsTab } from './components/ConferenceCallerControlsTab';

export default function ConferenceProfileFormView({
  onBack,
  onSave,
  onDelete,
  profile,
  mode
}: ConferenceProfileFormViewProps) {
  const navigate = useNavigate();
  const { t, refreshKey } = useI18n();
  const { isDebugModeActive } = useDebugMode();
  const { showSuccess, showError, showInfo } = useNotifications();

  const [formData, setFormData] = useState<FormConferenceProfile>(() => createEmptyProfile());
  const [originalFormData, setOriginalFormData] = useState<FormConferenceProfile>(() => createEmptyProfile());

  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [searchParams] = useSearchParams();

  // Track if profile has been applied to prevent loops
  const appliedProfileIdRef = useRef<number | string | null>(null);
  const lastIdRef = useRef<number | string | undefined>(formData.id);
  const lastResetParamRef = useRef<string | null>(null);

  // Initialize form data
  useEffect(() => {
    const profileId = profile?.id ?? profile?.uuid ?? null;

    // Only update if profile has actually changed (compare by ID, not object reference)
    if (profile && appliedProfileIdRef.current !== profileId) {
      const mapped = mapApiProfile(profile);
      setFormData(mapped);
      setOriginalFormData({
        ...mapped,
        settings: { ...mapped.settings }
      });
      appliedProfileIdRef.current = profileId;
      lastIdRef.current = mapped.id;
      return;
    }

    // Only reset to empty if profile is null and we haven't already done so
    if (!profile && appliedProfileIdRef.current !== null) {
      const emptyProfile = createEmptyProfile();
      setFormData(emptyProfile);
      setOriginalFormData({
        ...emptyProfile,
        settings: { ...emptyProfile.settings }
      });
      appliedProfileIdRef.current = null;
      lastIdRef.current = undefined;
    }
  }, [profile]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = formData.id;
    const previousId = lastIdRef.current;
    const currentResetParam = searchParams.get('_reset');

    // Also check for _reset query param which is set by useSaveWithAction
    const shouldReset = !currentId &&
      mode === 'create' &&
      (previousId !== undefined ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      const emptyProfile = createEmptyProfile();
      setFormData(emptyProfile);
      setOriginalFormData({
        ...emptyProfile,
        settings: { ...emptyProfile.settings }
      });
      appliedProfileIdRef.current = null;

      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [formData.id, mode, searchParams]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Handle form field changes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle settings changes
  type SettingsKey = keyof ConferenceProfileSettings;

  const handleSettingsChange = <K extends SettingsKey>(
    field: K,
    value: ConferenceProfileSettings[K]
  ) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value
      }
    }));
  };

  // Handle save
  const handleSave = useCallback(async (): Promise<ConferenceProfile | undefined> => {
    try {
      const completeSettings: ConferenceProfileSettings = {
        ...DEFAULT_PROFILE_SETTINGS,
        ...(formData.settings || {})
      };

      const settingsPayload: Record<string, unknown> = { ...completeSettings };

      const dataToSave = {
        name: formData.name,
        description: formData.description,
        enabled: formData.enabled,
        settings: settingsPayload
      };

      setLoading(true);
      const result = await onSave(dataToSave);
      showSuccess(t('conferenceProfiles:messages.saveSuccess'));

      // Update original data after successful save
      const updatedFormData = {
        ...formData,
        name: dataToSave.name,
        description: dataToSave.description,
        enabled: dataToSave.enabled,
        settings: { ...completeSettings }
      };

      setFormData(updatedFormData);
      setOriginalFormData({
        ...updatedFormData,
        settings: { ...updatedFormData.settings }
      });

      // Return saved data for useSaveWithAction
      if (result?.data?.id) {
        return result.data;
      }
      return undefined;
    } catch (error) {
      showError(error.message || t('conferenceProfiles:messages.saveError'));
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [formData, hasChanges, mode, onSave, showError, showInfo, showSuccess, t]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSave();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && mode === 'edit' && formData.id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(formData.id), uuid: String(formData.uuid || formData.id) } as ConferenceProfile;
    }

    return savedData;
  }, [handleSave, mode, formData]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/settings/pbx/conference-profiles',
    newPath: '/settings/pbx/conference-profiles/new',
    mode: mode,
    getEditPath: (savedData) => {
      // Conference Profiles use UUID or ID for navigation
      return `/settings/pbx/conference-profiles/${savedData.uuid ?? savedData.id}`;
    },
    getFormData: () => ({
      formData
    }),
    storageKey: 'conferenceProfiles:saveAndNewData',
  });

  // Handle cancel - restore original data (does NOT navigate)
  const handleCancel = () => {
    if (mode === 'edit') {
      setFormData({
        ...originalFormData,
        settings: { ...originalFormData.settings }
      });
      return;
    }

    const emptyProfile = createEmptyProfile();
    setFormData(emptyProfile);
    setOriginalFormData({
      ...emptyProfile,
      settings: { ...emptyProfile.settings }
    });
  };

  // Handle delete - show confirmation modal
  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  // Confirm delete - actually delete the item and navigate to list
  const confirmDelete = async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete();
      showSuccess(t('conferenceProfiles:messages.deleteSuccess'));
      setShowDeleteModal(false);
      // Navigation will be handled by parent component
    } catch (error) {
      showError(error.message || t('conferenceProfiles:messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle view XML
  // Quick List state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListProfiles, setQuickListProfiles] = useState<FormConferenceProfile[]>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const rawProfiles = await fetchAllConferenceProfiles();
      setQuickListProfiles(rawProfiles.map(mapApiProfile));
    } catch (error) {
      showError(error?.message || t('conferenceProfiles:messages.loadError'));
      setQuickListProfiles([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [showError, t]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListProfiles.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, loadQuickList]);

  // Filter profiles client-side
  const filteredQuickList = useMemo(() => {
    const profiles = quickListProfiles.filter(p => p.id != null);
    if (!quickListSearch.trim()) {
      return profiles;
    }
    const searchLower = quickListSearch.toLowerCase();
    return profiles.filter(
      (profile) =>
        profile.name?.toLowerCase().includes(searchLower) ||
        profile.description?.toLowerCase().includes(searchLower)
    );
  }, [quickListProfiles, quickListSearch]);

  // Handle duplicate from form
  const handleDuplicateFromForm = useCallback(() => {
    if (mode === 'edit' && formData.id) {
      navigateToConferenceProfileDuplicate(navigate, formData.id);
    }
  }, [mode, formData.id, navigate]);

  // Tab configuration

  return (
    <div className="p-6 pb-20">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Users className="h-5 w-5 text-primary" />}
        title={mode === 'create'
          ? t('conferenceProfiles:form.createTitle')
          : `${t('conferenceProfiles:form.editTitle')} - ${formData.name || ''}`
        }
        backButtonText={t('conferenceProfiles:actions.list')}
        onBack={onBack}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('conferenceProfiles:actions.quickList'),
            onClick: () => setShowQuickList(true)
          },
          ...(mode === 'edit' && formData.id
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: handleDuplicateFromForm
              }
            ]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('conferenceProfiles:actions.add'),
            onClick: () => navigate('/settings/pbx/conference-profiles/new')
          }
        ]}
      />

      {/* Tabs */}
      <div className="mt-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1">
            <TabsTrigger value="basic">
              {t('conferenceProfiles:tabs.basic')}
            </TabsTrigger>
            <TabsTrigger value="audio">
              {t('conferenceProfiles:tabs.audio')}
            </TabsTrigger>
            <TabsTrigger value="video">
              {t('conferenceProfiles:tabs.video')}
            </TabsTrigger>
            <TabsTrigger value="sounds">
              {t('conferenceProfiles:tabs.sounds')}
            </TabsTrigger>
            <TabsTrigger value="advanced">
              {t('conferenceProfiles:tabs.advanced')}
            </TabsTrigger>
            <TabsTrigger value="caller-controls">
              {t('conferenceProfiles:tabs.callerControls')}
            </TabsTrigger>
          </TabsList>
          {/* Basic Tab */}
          <TabsContent value="basic">
            <div className="space-y-4">
              {/* Basic Information */}
              <FormBox
                title={t('conferenceProfiles:fields.basicInformation')}
                icon={<Settings className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                  {/* Row 1: Name | Input | Description | Input */}
                  <SimpleTooltip
                    content={isDebugModeActive ? `${t('conferenceProfiles:fields.name.tooltip')}\n\n${t('conferenceProfiles:debug.field')}: name` : t('conferenceProfiles:fields.name.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.name.label')} *
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder={t('conferenceProfiles:fields.name.placeholder')}
                    required
                  />
                  <SimpleTooltip
                    content={isDebugModeActive ? `${t('conferenceProfiles:fields.description.tooltip')}\n\n${t('conferenceProfiles:debug.field')}: description` : t('conferenceProfiles:fields.description.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.description.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder={t('conferenceProfiles:fields.description.placeholder')}
                  />

                  {/* Row 2: Enabled | Toggle | System Profile | Info */}
                  <SimpleTooltip
                    content={isDebugModeActive ? `${t('conferenceProfiles:fields.enabled.tooltip')}\n\n${t('conferenceProfiles:debug.field')}: enabled` : t('conferenceProfiles:fields.enabled.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.enabled.label')}
                    </Label>
                  </SimpleTooltip>
                  <div className="flex justify-start">
                    <Toggle
                      value={formData.enabled}
                      onChange={(value) => handleFieldChange('enabled', value)}
                    />
                  </div>
                  {formData.isSystem && (
                    <>
                      <Label className="text-left">
                        {t('conferenceProfiles:fields.isSystem.label')}
                      </Label>
                      <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                        <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                        {t('conferenceProfiles:fields.isSystem.description')}
                      </div>
                    </>
                  )}
                </div>
              </FormBox>

              {/* Basic Audio Settings */}
              <FormBox
                title={t('conferenceProfiles:sections.basicAudio')}
                icon={<Settings className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Sample Rate */}
                  <div className="space-y-2">
                    <SimpleTooltip
                      content={isDebugModeActive ? `${t('conferenceProfiles:fields.rate.tooltip')}\n\n${t('conferenceProfiles:debug.field')}: ${t('conferenceProfiles:debug.rate')}` : t('conferenceProfiles:fields.rate.tooltip')}
                      position="top"
                      maxWidth="300px"
                    >
                      <Label className="text-sm font-medium">
                        {t('conferenceProfiles:fields.rate.label')}
                      </Label>
                    </SimpleTooltip>
                    <select
                      value={formData.settings.rate}
                      onChange={(e) => handleSettingsChange('rate', e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="8000">8000 Hz (Narrowband)</option>
                      <option value="16000">16000 Hz (Wideband)</option>
                      <option value="32000">32000 Hz (Ultra-wideband)</option>
                      <option value="48000">48000 Hz (CD Quality)</option>
                    </select>
                  </div>

                  {/* Channels */}
                  <div className="space-y-2">
                    <SimpleTooltip
                      content={t('conferenceProfiles:fields.channels.tooltip')}
                      position="top"
                      maxWidth="300px"
                    >
                      <Label className="text-sm font-medium">
                        {t('conferenceProfiles:fields.channels.label')}
                      </Label>
                    </SimpleTooltip>
                    <select
                      value={formData.settings.channels}
                      onChange={(e) => handleSettingsChange('channels', e.target.value)}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="1">Mono</option>
                      <option value="2">Stereo</option>
                    </select>
                  </div>

                  {/* Energy Level */}
                  <div className="space-y-2">
                    <SimpleTooltip
                      content={t('conferenceProfiles:fields.energyLevel.tooltip')}
                      position="top"
                      maxWidth="300px"
                    >
                      <Label className="text-sm font-medium">
                        {t('conferenceProfiles:fields.energyLevel.label')}
                      </Label>
                    </SimpleTooltip>
                    <Input
                      type="number"
                      value={formData.settings.energyLevel}
                      onChange={(e) => {
                        const numericValue = Number(e.target.value);
                        handleSettingsChange(
                          'energyLevel',
                          Number.isNaN(numericValue) ? DEFAULT_PROFILE_SETTINGS.energyLevel : numericValue
                        );
                      }}
                      placeholder="100"
                    />
                  </div>
                </div>
              </FormBox>
            </div>
          </TabsContent>

          {/* Audio Tab */}
          <TabsContent value="audio">
            <div className="space-y-4">
              <FormBox
                title={t('conferenceProfiles:tabs.audio')}
                icon={<Settings className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                  {/* Row 1: MOH Sound | Input | Enter Sound | Input */}
                  <SimpleTooltip
                    content={isDebugModeActive ? `${t('conferenceProfiles:fields.mohSound.tooltip')}\n\n${t('conferenceProfiles:debug.field')}: ${t('conferenceProfiles:debug.mohSound')}` : t('conferenceProfiles:fields.mohSound.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.mohSound.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.mohSound}
                    onChange={(e) => handleSettingsChange('mohSound', e.target.value)}
                    placeholder="$${hold_music}"
                  />
                  <SimpleTooltip
                    content={isDebugModeActive ? `${t('conferenceProfiles:fields.enterSound.tooltip')}\n\n${t('conferenceProfiles:debug.field')}: ${t('conferenceProfiles:debug.enterSound')}` : t('conferenceProfiles:fields.enterSound.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.enterSound.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.enterSound}
                    onChange={(e) => handleSettingsChange('enterSound', e.target.value)}
                    placeholder="tone_stream://%(200,0,500,600,700)"
                  />

                  {/* Row 2: Exit Sound | Input | Comfort Noise | Toggle */}
                  <SimpleTooltip
                    content={isDebugModeActive ? `${t('conferenceProfiles:fields.exitSound.tooltip')}\n\n${t('conferenceProfiles:debug.field')}: ${t('conferenceProfiles:debug.exitSound')}` : t('conferenceProfiles:fields.exitSound.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.exitSound.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.exitSound}
                    onChange={(e) => handleSettingsChange('exitSound', e.target.value)}
                    placeholder="tone_stream://%(500,0,300,200,100,50,25)"
                  />
                  <SimpleTooltip
                    content={isDebugModeActive ? `${t('conferenceProfiles:fields.comfortNoise.tooltip')}\n\n${t('conferenceProfiles:debug.field')}: ${t('conferenceProfiles:debug.comfortNoise')}` : t('conferenceProfiles:fields.comfortNoise.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.comfortNoise.label')}
                    </Label>
                  </SimpleTooltip>
                  <div className="flex justify-start">
                    <Toggle
                      value={formData.settings.comfortNoise}
                      onChange={(value) => handleSettingsChange('comfortNoise', value)}
                    />
                  </div>

                </div>
              </FormBox>
            </div>
          </TabsContent>

          {/* Video Tab */}
          <TabsContent value="video">
            <div className="space-y-4">
              <FormBox
                title={t('conferenceProfiles:tabs.video')}
                icon={<Settings className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                  {/* Row 1: Video Mode | Select | Video Layout | Select */}
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.videoMode.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.videoMode.label')}
                    </Label>
                  </SimpleTooltip>
                  <select
                    value={formData.settings.videoMode}
                    onChange={(e) => handleSettingsChange('videoMode', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="auto">Auto</option>
                    <option value="mux">Mux</option>
                    <option value="none">None</option>
                  </select>
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.videoLayout.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.videoLayout.label')}
                    </Label>
                  </SimpleTooltip>
                  <select
                    value={formData.settings.videoLayout}
                    onChange={(e) => handleSettingsChange('videoLayout', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="1x1">1x1</option>
                    <option value="2x2">2x2</option>
                    <option value="3x3">3x3</option>
                    <option value="4x4">4x4</option>
                  </select>

                  {/* Row 2: Video Codec | Select | Max Video Participants | Input */}
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.videoCodec.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.videoCodec.label')}
                    </Label>
                  </SimpleTooltip>
                  <select
                    value={formData.settings.videoCodec}
                    onChange={(e) => handleSettingsChange('videoCodec', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="VP8">VP8</option>
                    <option value="VP9">VP9</option>
                    <option value="H264">H.264</option>
                    <option value="H265">H.265</option>
                  </select>
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.maxVideoParticipants.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.maxVideoParticipants.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    type="number"
                    value={formData.settings.maxVideoParticipants}
                    onChange={(e) => {
                      const numericValue = Number(e.target.value);
                      handleSettingsChange(
                        'maxVideoParticipants',
                        Number.isNaN(numericValue) ? DEFAULT_PROFILE_SETTINGS.maxVideoParticipants : numericValue
                      );
                    }}
                    placeholder="9"
                  />
                </div>
              </FormBox>
            </div>
          </TabsContent>

          {/* Sounds Tab */}
          <TabsContent value="sounds">
            <div className="space-y-4">
              <FormBox
                title={t('conferenceProfiles:tabs.sounds')}
                icon={<Settings className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                  {/* Row 1: Muted Sound | Input | Unmuted Sound | Input */}
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.mutedSound.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.mutedSound.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.mutedSound}
                    onChange={(e) => handleSettingsChange('mutedSound', e.target.value)}
                    placeholder="conference/conf-muted.wav"
                  />
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.unmutedSound.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.unmutedSound.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.unmutedSound}
                    onChange={(e) => handleSettingsChange('unmutedSound', e.target.value)}
                    placeholder="conference/conf-unmuted.wav"
                  />

                  {/* Row 2: Alone Sound | Input | Kicked Sound | Input */}
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.aloneSound.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.aloneSound.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.aloneSound}
                    onChange={(e) => handleSettingsChange('aloneSound', e.target.value)}
                    placeholder="conference/conf-alone.wav"
                  />
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.kickedSound.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.kickedSound.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.kickedSound}
                    onChange={(e) => handleSettingsChange('kickedSound', e.target.value)}
                    placeholder="conference/conf-kicked.wav"
                  />
                </div>
              </FormBox>
            </div>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced">
            <div className="space-y-4">
              <FormBox
                title={t('conferenceProfiles:tabs.advanced')}
                icon={<Settings className="h-5 w-5" />}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                  {/* Row 1: Conference Flags | Input | Caller Controls | Select */}
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.conferenceFlags.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.conferenceFlags.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.conferenceFlags}
                    onChange={(e) => handleSettingsChange('conferenceFlags', e.target.value)}
                    placeholder="mintwo,moderator"
                  />
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.callerControls.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.callerControls.label')}
                    </Label>
                  </SimpleTooltip>
                  <select
                    value={formData.settings.callerControls}
                    onChange={(e) => handleSettingsChange('callerControls', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="default">Default</option>
                    <option value="none">None</option>
                    <option value="custom">Custom</option>
                  </select>

                  {/* Row 2: Moderator Controls | Toggle | Member Flags | Input */}
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.moderatorControls.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.moderatorControls.label')}
                    </Label>
                  </SimpleTooltip>
                  <div className="flex justify-start">
                    <Toggle
                      value={formData.settings.moderatorControls}
                      onChange={(value) => handleSettingsChange('moderatorControls', value)}
                    />
                  </div>
                  <SimpleTooltip
                    content={t('conferenceProfiles:fields.memberFlags.tooltip')}
                    position="top"
                    maxWidth="300px"
                  >
                    <Label className="text-left">
                      {t('conferenceProfiles:fields.memberFlags.label')}
                    </Label>
                  </SimpleTooltip>
                  <Input
                    value={formData.settings.memberFlags}
                    onChange={(e) => handleSettingsChange('memberFlags', e.target.value)}
                    placeholder="waste,mute,deaf"
                  />
                </div>
              </FormBox>
            </div>
          </TabsContent>

          {/* Caller Controls Tab */}
          <TabsContent value="caller-controls">
            <ConferenceCallerControlsTab
              settings={formData.settings}
              onSettingsChange={handleSettingsChange}
              t={t}
            />
          </TabsContent>
        </Tabs>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={mode === 'edit' && onDelete ? handleDelete : undefined}
        isEditMode={mode === 'edit'}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete') || 'Delete'}
        isLoading={loading}
        useSaveDropdown={true}
        saveDropdownStorageKey="conferenceProfiles:saveActionPreference"
        hasChanges={hasChanges}
      />

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('conferenceProfiles:actions.quickList', 'Conference Profiles')}
        items={filteredQuickList as any}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => {
          setShowQuickList(false);
          navigate(`/settings/pbx/conference-profiles/${profile.uuid ?? profile.id}`);
        }}
        currentItemId={mode === 'edit' ? formData.id : null}
        renderItem={(profile, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{profile.name}</p>
              <p className="text-sm text-muted-foreground">
                {profile.description || t('conferenceProfiles:noDescription', 'No description')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {profile.isSystem && (
                <span className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 rounded">
                  {t('conferenceProfiles:system', 'System')}
                </span>
              )}
              {isCurrent && (
                <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                  {t('conferenceProfiles:current', 'Current')}
                </span>
              )}
            </div>
          </div>
        )}
        searchPlaceholder={t('conferenceProfiles:quickListSearch', 'Search by name or description')}
        emptyMessage={t('conferenceProfiles:quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('conferenceProfiles:quickListNoResults', 'No profiles found')}
        headerIcon={<Users className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('conferenceProfiles:deleteConfirmTitle') || 'Delete Conference Profile'}
        message={t('conferenceProfiles:deleteConfirmMessage') || `Are you sure you want to delete this conference profile? This action cannot be undone.`}
        confirmText={t('conferenceProfiles:deleteConfirm') || t('common.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
